import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFeatured, TYPE_LABELS } from "@/lib/media";

export default function HeroCarousel() {
  const featured = getFeatured();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || featured.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, featured.length]);

  if (featured.length === 0) return null;
  const active = featured[index];

  return (
    <section className="shell pt-5 md:pt-7" aria-label="焦点推荐">
      <div
        className="relative h-[440px] w-full overflow-hidden rounded-lg bg-foreground-950 sm:h-[480px] md:h-[560px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {featured.map((movie, i) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={i !== index}
          >
            <img
              src={movie.backdrop ?? movie.poster}
              alt={`《${movie.title}》焦点剧照`}
              title={`${movie.title} 剧情介绍`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/75 to-foreground-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-transparent to-foreground-950/25" />
        <Link
          to={`/detail/${active.id}`}
          aria-label={`查看《${active.title}》详情`}
          className="absolute inset-0 z-[1]"
        />

        <div className="relative z-10 flex h-full flex-col justify-end px-5 pb-8 pt-10 sm:px-8 md:justify-center md:px-12 md:pb-16 md:pt-10">
          <div key={active.id} className="anim-slide-right max-w-xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-foreground-950">
                <i className="ri-fire-line text-xs" />
                本周焦点
              </span>
              <span className="rounded-md border border-background-50/30 px-2.5 py-1 text-[11px] font-medium text-background-100">
                {TYPE_LABELS[active.type]}
              </span>
              <span className="text-[11px] font-medium text-background-300">{active.status}</span>
            </div>

            <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-background-50 sm:text-4xl md:text-[44px]">
              {active.title}
            </h1>
            <p className="mt-1.5 font-label text-xs uppercase tracking-[0.2em] text-background-300">
              {active.enTitle}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-background-200 sm:text-sm">
              <span className="flex items-center gap-1 font-semibold text-accent-400">
                <i className="ri-star-fill text-xs" />
                {active.rating.toFixed(1)}
              </span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{active.year}</span>
              <span>{active.region}</span>
              <span>{active.duration}</span>
              <span className="hidden sm:inline">导演 {active.director}</span>
            </div>

            <p className="mt-4 line-clamp-2 max-w-lg text-sm leading-relaxed text-background-200 md:line-clamp-3">
              {active.synopsis[0]}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-200"
              >
                <i className="ri-play-circle-line text-base" />
                了解详情
              </Link>
              <Link
                to={`/category/${active.type}`}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                更多{TYPE_LABELS[active.type]}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>

          <div className="absolute bottom-5 right-5 hidden items-center gap-3 md:flex">
            <button
              type="button"
              onClick={() => setIndex((prev) => (prev - 1 + featured.length) % featured.length)}
              aria-label="上一个"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => setIndex((prev) => (prev + 1) % featured.length)}
              aria-label="下一个"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>

        <div className="absolute bottom-6 left-5 z-10 hidden items-center gap-2 sm:left-8 md:bottom-8 md:left-12 md:flex">
          {featured.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到 ${movie.title}`}
              aria-current={i === index}
              className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
          <span className="ml-2 font-label text-[11px] tracking-widest text-background-200">
            {String(index + 1).padStart(2, "0")} / {String(featured.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}